import { useCallback, useEffect, useMemo, useRef } from "react";
import isEqual from "lodash/isEqual";
import { AgeRangeAvailableBounds, ProductSearchRequest } from "reedy-data/api";
import { useSearchQuery } from "hooks/search/useSearchQuery";
import { useSearch } from "hooks/search/useSearch";
import { useSearchAll } from "hooks/search/useSearchAll";
import {
  SearchProductWithScore,
  SearchQuery,
  SearchResult,
  SearchResultHit,
  SearchType,
} from "hooks/search/types";
import { buildCatalogPayload } from "pages/Books/analytics/buildCatalogPayload";
import { useCatalogAnalytics } from "pages/Books/analytics/useCatalogAnalytics";

const flattenHit = (hit: SearchResultHit): SearchProductWithScore => ({
  ...hit.product,
  score: hit.score,
  matched_queries: hit.matched_queries,
});

interface Data {
  total: number | null;
  count: number;
  products: SearchProductWithScore[];
}

interface UseBacklistDataReturn {
  data: Data;
  isLoading: boolean;
  error?: Error | null;
  searchQuery: SearchQuery;
  searchRequestParams: ProductSearchRequest;
  ageRangeAvailableBounds: AgeRangeAvailableBounds | null;
  setSearchQuery(query: Partial<SearchQuery>): void;
  fetchAllResults(): Promise<SearchProductWithScore[]>;
}

export const useBacklistData = (): UseBacklistDataReturn => {
  const { query: searchQuery, setQuery: setSearchQuery } = useSearchQuery();
  const { searchAll } = useSearchAll();
  const { emit: catalogEmit, notifyFetchSettled } = useCatalogAnalytics();

  // Unfiltered total uses query "" to match browse; backend treats it as match_all either way.
  const { data: totalResponse } = useSearch({
    query: "",
    type: SearchType.QUICK,
  });

  const prevSearchQueryRef = useRef<SearchQuery | null>(null);
  useEffect(() => {
    const prev = prevSearchQueryRef.current;
    prevSearchQueryRef.current = searchQuery;
    if (!prev) return;

    const prevPage = prev.page ?? 1;
    const nextPage = searchQuery.page ?? 1;
    if (prevPage === nextPage) return;

    const onlyPageChanged =
      prev.query === searchQuery.query &&
      prev.type === searchQuery.type &&
      (prev.pageSize ?? null) === (searchQuery.pageSize ?? null) &&
      isEqual(prev.filters, searchQuery.filters);

    if (onlyPageChanged) {
      catalogEmit.paginated(prevPage, buildCatalogPayload(searchQuery));
    }
  }, [searchQuery, catalogEmit]);

  const onSettled = useCallback(
    (response: SearchResult | undefined, snapshot: SearchQuery) => {
      notifyFetchSettled(response, buildCatalogPayload(snapshot));
    },
    [notifyFetchSettled],
  );

  const {
    error,
    data: searchResponse,
    isLoading,
    requestParams: searchRequestParams,
    isPlaceholderData,
  } = useSearch(searchQuery, { onSettled, includeAgeRangeBounds: true });

  const data = useMemo(
    () => ({
      total: totalResponse?.found ?? null,
      count: searchResponse?.found ?? 0,
      products: searchResponse?.hits?.map(flattenHit) ?? [],
    }),
    [searchResponse, totalResponse],
  );

  const fetchAllResults = useCallback(async (): Promise<
    SearchProductWithScore[]
  > => {
    if (data.products.length >= data.count && data.count > 0) {
      return data.products;
    }

    const hits = await searchAll(searchRequestParams);
    return hits.map(flattenHit);
  }, [searchAll, searchRequestParams, data.products, data.count]);

  return {
    data,
    isLoading: isLoading || isPlaceholderData,
    error,
    searchQuery,
    searchRequestParams,
    // Hide bounds while refetching so the drawer never shows stale bounds from the prior filter set.
    ageRangeAvailableBounds: isPlaceholderData
      ? null
      : (searchResponse?.ageRangeAvailableBounds ?? null),
    setSearchQuery,
    fetchAllResults,
  };
};
